import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowRight,
  Bot,
  Star,
  BookOpen,
  Volume2,
  Lock,
  Sparkles,
  Gem,
  Unlock,
} from "lucide-react";
import { Link } from "react-router-dom";

interface MeetingCardProps {
  id: string;
  name: string;
  category: string;
  level?: "Beginner" | "Intermediate" | "Advanced";
  openAccess?: boolean;
  premiumUnlocked?: boolean;
}
export const MeetingCard = ({
  id,
  name,
  category,
  level,
  openAccess,
  premiumUnlocked,
}: MeetingCardProps) => {
  const getLevelStyles = (level?: string) => {
    switch (level) {
      case "Beginner":
        return "border-green-600 text-green-600 bg-green-50 dark:bg-green-950/30 dark:text-green-400 dark:border-green-600";
      case "Intermediate":
        return "border-yellow-600 text-yellow-600 bg-yellow-50 dark:bg-yellow-950/30 dark:text-yellow-400 dark:border-yellow-600";
      case "Advanced":
        return "border-orange-600 text-orange-600 bg-orange-50 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-600";
      default:
        return "border-green-600 text-green-600 bg-green-50 dark:bg-green-950/30 dark:text-green-400 dark:border-green-600";
    }
  };

  return (
    <Link to={`/meeting/${id}`}>
      <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-[var(--shadow-hover)] border-border/50 backdrop-blur-sm bg-card/80 animate-fade-in">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors flex-1 h-[5.6rem] line-clamp-4 leading-relaxed">
              {name}
            </h3>
            <div className="flex flex-col gap-2 items-end">
              {/* Access indicator */}
              {premiumUnlocked ? (
                // Premium unlocked - show unlocked badge
                <Badge
                  variant="secondary"
                  className="bg-gradient-to-r from-emerald-500 to-green-500 text-white border-0 shadow-sm text-sm px-4 py-1 h-10"
                >
                  <Gem className="w-6 h-6 mr-1" />
                  Unlocked
                </Badge>
              ) : openAccess ? (
                // Originally free - show Free badge
                <Badge
                  variant="secondary"
                  className="bg-gradient-to-r from-emerald-500 to-green-500 text-white border-0 shadow-sm text-sm px-4 py-1 h-10"
                >
                  <Sparkles className="w-6 h-6 mr-1" />
                  Free
                </Badge>
              ) : (
                // Locked - show lock
                <div className="flex items-center gap-1 px-4 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium h-10">
                  <Lock className="w-6 h-6" />
                </div>
              )}

              {/* Level badge (commented out in original) */}
              {/* {level && <Badge variant="outline" className={`text-[10px] px-2 py-0 h-5 ${getLevelStyles(level)}`}>
                {level}
              </Badge>} */}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
              <ArrowRight className="w-4 h-4 text-primary group-hover:text-primary-foreground transition-colors" />
            </div>
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </Card>
    </Link>
  );
};
